import gql from "graphql-tag";
import {useCallback} from "react";

import type {Result} from "@khanacademy/wonder-blocks-data";
import {useMutation} from "@repo/data-gql";
import {gqlOp, type GqlOperation} from "@repo/data-gql-core";
import {Errors, KAError} from "@repo/logging";

import type {
    aiGuideApplySwagMutation,
    aiGuideApplySwagMutationVariables,
} from "./__graphql-types__/aiGuideApplySwagMutation.ts";
import type {aiGuideApplySwagMutationType} from "./__graphql-types__/index.ts";

const AI_GUIDE_APPLY_SWAG_MUTATION: GqlOperation<
    aiGuideApplySwagMutationType["response"],
    aiGuideApplySwagMutationType["variables"]
> = gqlOp(gql`
    mutation aiGuideApplySwagMutation($kaid: String!, $swagId: String!) {
        applyAiGuideSwag(kaid: $kaid, swagID: $swagId) {
            error {
                code
            }
        }
    }
`);

/**
 * Returns a mutation function responsible for applying the change of item.
 */
export const useApplyGuideClosetItemMutation = (): [
    (variables: aiGuideApplySwagMutationVariables) => Promise<void>,
    Result<aiGuideApplySwagMutation>["status"] | null,
] => {
    const [mutate, result] = useMutation(AI_GUIDE_APPLY_SWAG_MUTATION);

    const applyItem = useCallback(
        async (variables: aiGuideApplySwagMutationVariables) => {
            try {
                const result = await mutate(variables);

                if (result.status !== "success") {
                    throw new KAError(
                        "Failed to call aiGuideApplySwagMutation",
                        Errors.GraphqlResponse,
                    );
                }

                const error = result.data?.applyAiGuideSwag?.error;
                if (error) {
                    throw new KAError(
                        "aiGuideApplySwagMutation succeeded with a failure",
                        Errors.GraphqlResponse,
                        {
                            cause: error.code,
                        },
                    );
                }
            } catch (error) {
                // We handle the exception thrown from within the mutation success
                // handler.
                if (error instanceof KAError) {
                    throw error;
                }
                // Otherwise we throw a generic error.
                throw new KAError(
                    "Failed to call aiGuideApplySwagMutation",
                    Errors.Internal,
                );
            }
        },
        [mutate],
    );

    return [applyItem, result?.status ?? null];
};
